/**
 * Termodinâmica do Skew-T — funções puras (testadas em `termo.test.ts`).
 *
 * As fórmulas e as convenções seguem o MetPy (Unidata), que é a referência dos
 * scripts de Skew-T: pressão de vapor de Bolton (1980), adiabática úmida
 * pseudo-adiabática, parcela da camada de mistura (500 m) levantada a seco
 * até o NCL e saturada dali para cima, e CAPE/CIN pela área entre a parcela e o ambiente em ln(p), SEM
 * correção de temperatura virtual (o padrão do `metpy.calc.cape_cin`).
 *
 * Unidades: pressão em hPa, temperatura em °C na interface e em K por dentro.
 */
import type { Nivel } from './types';

export const RD = 287.047;
export const CP = 1004.666;
export const KAPPA = RD / CP;
export const EPSILON = 0.62196;
export const LV = 2.50084e6;
const ZERO_K = 273.15;

/** Pressão de vapor de saturação (hPa) sobre água, Bolton (1980). */
export function pressaoDeSaturacao(tC: number): number {
  return 6.112 * Math.exp((17.67 * tC) / (tC + 243.5));
}

/** Razão de mistura (kg/kg) de um ar com pressão de vapor `e` (hPa) na pressão `p` (hPa). */
export function razaoDeMistura(e: number, p: number): number {
  return (EPSILON * e) / (p - e);
}

/** Razão de mistura de saturação (kg/kg). */
export function misturaSaturada(p: number, tC: number): number {
  return razaoDeMistura(pressaoDeSaturacao(tC), p);
}

/** Ponto de orvalho (°C) do ar com razão de mistura `w` (kg/kg) na pressão `p`: a inversa de Bolton. */
export function orvalhoDaMistura(p: number, w: number): number {
  const e = (w * p) / (EPSILON + w);
  const l = Math.log(e / 6.112);
  return (243.5 * l) / (17.67 - l);
}

/** Temperatura (°C) na pressão `p` de uma parcela seca que tinha `tC` em `p0`. */
export function adiabaticaSeca(tC: number, p0: number, p: number): number {
  return (tC + ZERO_K) * Math.pow(p / p0, KAPPA) - ZERO_K;
}

/** dT/d(ln p) da pseudo-adiabática saturada (K), a mesma expressão do `moist_lapse` do MetPy. */
function taxaUmida(tK: number, p: number): number {
  const rs = misturaSaturada(p, tK - ZERO_K);
  return (RD * tK + LV * rs) / (CP + (LV * LV * rs * EPSILON) / (RD * tK * tK));
}

/**
 * Temperatura (°C) na pressão `p` de uma parcela SATURADA que tinha `tC` em
 * `p0`. Runge-Kutta de 4ª ordem em ln(p), em passos de no máximo ~10 hPa a
 * 1000 hPa — o erro fica abaixo de 0,01 K até 100 hPa.
 */
export function adiabaticaUmida(tC: number, p0: number, p: number): number {
  const total = Math.log(p / p0);
  const n = Math.max(1, Math.ceil(Math.abs(total) / 0.01));
  const h = total / n;
  let t = tC + ZERO_K;
  let lnp = Math.log(p0);
  for (let i = 0; i < n; i++) {
    const k1 = taxaUmida(t, Math.exp(lnp));
    const k2 = taxaUmida(t + (h * k1) / 2, Math.exp(lnp + h / 2));
    const k3 = taxaUmida(t + (h * k2) / 2, Math.exp(lnp + h / 2));
    const k4 = taxaUmida(t + h * k3, Math.exp(lnp + h));
    t += (h * (k1 + 2 * k2 + 2 * k3 + k4)) / 6;
    lnp += h;
  }
  return t - ZERO_K;
}

export interface Ponto {
  p: number;
  /** °C. */
  t: number;
}

/** Nível de condensação por levantamento (Bolton 1980, eq. 15 e a adiabática seca). */
export function ncl(p: number, tC: number, dC: number): Ponto {
  const tK = tC + ZERO_K;
  const dK = Math.min(tK, dC + ZERO_K);
  const tl = 56 + 1 / (1 / (dK - 56) + Math.log(tK / dK) / 800);
  return { p: p * Math.pow(tl / tK, 1 / KAPPA), t: tl - ZERO_K };
}

/** Temperatura potencial equivalente (K), Bolton (1980) eq. 39 — para achar a parcela mais instável. */
export function thetaE(p: number, tC: number, dC: number): number {
  const tK = tC + ZERO_K;
  const e = pressaoDeSaturacao(Math.min(tC, dC));
  const r = razaoDeMistura(e, p);
  const tl = ncl(p, tC, dC).t + ZERO_K;
  const thetaSeca = tK * Math.pow(1000 / (p - e), KAPPA) * Math.pow(tK / tl, 0.28 * r);
  return thetaSeca * Math.exp((3036 / tl - 1.78) * r * (1 + 0.448 * r));
}

/** Níveis com pressão, temperatura e orvalho, da base para o topo, sem repetidos. */
export function niveisCompletos(niveis: readonly Nivel[]): { p: number; t: number; d: number; z: number }[] {
  const out: { p: number; t: number; d: number; z: number }[] = [];
  for (const n of [...niveis].sort((a, b) => b.p - a.p)) {
    if (n.t === null || n.d === null || !(n.p > 0) || !Number.isFinite(n.t) || !Number.isFinite(n.d)) continue;
    if (out.length && out[out.length - 1]!.p === n.p) continue;
    out.push({ p: n.p, t: n.t, d: n.d, z: n.z });
  }
  return out;
}

/** Interpolação linear em ln(p) de uma grandeza do ambiente; fora da faixa medida, `null`. */
export function noAmbiente(niveis: readonly { p: number; t: number; d: number }[], p: number, campo: 't' | 'd' = 't'): number | null {
  if (!niveis.length || p > niveis[0]!.p || p < niveis[niveis.length - 1]!.p) return null;
  for (let i = 1; i < niveis.length; i++) {
    const a = niveis[i - 1]!;
    const b = niveis[i]!;
    if (p < b.p) continue;
    const span = Math.log(a.p) - Math.log(b.p);
    const k = span === 0 ? 0 : (Math.log(a.p) - Math.log(p)) / span;
    return a[campo] + (b[campo] - a[campo]) * k;
  }
  return niveis[niveis.length - 1]![campo];
}

/** Camada em que se procura a parcela mais instável, a partir da superfície (hPa) — o padrão do MetPy. */
export const CAMADA_MAIS_INSTAVEL = 300;

/** A parcela mais instável: o nível de maior theta-e nos primeiros 300 hPa. */
export function parcelaMaisInstavel(niveis: readonly { p: number; t: number; d: number }[]): { p: number; t: number; d: number } | null {
  if (!niveis.length) return null;
  const base = niveis[0]!.p;
  let melhor: { p: number; t: number; d: number } | null = null;
  let maior = -Infinity;
  for (const n of niveis) {
    if (n.p < base - CAMADA_MAIS_INSTAVEL) break;
    const te = thetaE(n.p, n.t, n.d);
    if (te > maior) {
      maior = te;
      melhor = n;
    }
  }
  return melhor;
}

/** Passo da grade vertical da integração, em ln(p): ~5 hPa a 1000 hPa. */
const PASSO_LNP = 0.005;

export interface Parcela {
  /** De onde a parcela sai. */
  origem: { p: number; t: number; d: number };
  ncl: Ponto;
  /** Nível de convecção espontânea: onde a parcela passa a ser mais quente que o ar. `null` = não há. */
  nce: Ponto | null;
  /** Nível de equilíbrio: onde ela deixa de ser. `null` = não há (ou fica acima do perfil). */
  ne: Ponto | null;
  /** Energia disponível (J/kg, ≥ 0) e inibição (J/kg, ≤ 0). */
  cape: number;
  cin: number;
  /** A curva da parcela e a do ambiente na mesma grade (da origem ao topo do perfil). */
  curva: { p: number; parcela: number; ambiente: number }[];
}

/**
 * Levanta a parcela `origem` pelo perfil e mede as áreas de energia.
 *
 * Como no MetPy (`cape_cin` com `which_lfc='bottom'`, `which_el='top'`): a CAPE
 * soma as áreas positivas entre o NCE mais baixo e o NE mais alto; a CIN, as
 * negativas entre a origem e o NCE. Sem NCE não há CAPE nem CIN.
 */
export function levantar(niveis: readonly { p: number; t: number; d: number }[], origem: { p: number; t: number; d: number }, pTopo = 100): Parcela | null {
  if (niveis.length < 3) return null;
  const topo = Math.max(pTopo, niveis[niveis.length - 1]!.p);
  if (!(origem.p > topo)) return null;
  const c = ncl(origem.p, origem.t, origem.d);

  const curva: Parcela['curva'] = [];
  const n = Math.max(2, Math.ceil(Math.log(origem.p / topo) / PASSO_LNP));
  // A pseudo-adiabática é integrada de ponto em ponto, e não do NCL a cada vez.
  let pAnt = c.p;
  let tAnt = c.t;
  for (let i = 0; i <= n; i++) {
    const p = origem.p * Math.pow(topo / origem.p, i / n);
    const ambiente = noAmbiente(niveis, p);
    if (ambiente === null) continue;
    let parcela: number;
    if (p >= c.p) parcela = adiabaticaSeca(origem.t, origem.p, p);
    else {
      parcela = adiabaticaUmida(tAnt, pAnt, p);
      pAnt = p;
      tAnt = parcela;
    }
    curva.push({ p, parcela, ambiente });
  }
  if (curva.length < 3) return null;

  /** Ponto em que a diferença troca de sinal entre `a` e `b`, interpolado em ln(p). */
  const cruzamento = (a: Parcela['curva'][number], b: Parcela['curva'][number]): Ponto => {
    const da = a.parcela - a.ambiente;
    const db = b.parcela - b.ambiente;
    const k = da === db ? 0 : da / (da - db);
    return { p: Math.exp(Math.log(a.p) + (Math.log(b.p) - Math.log(a.p)) * k), t: a.parcela + (b.parcela - a.parcela) * k };
  };

  // NCE: a primeira vez, do NCL para cima, em que a parcela fica mais quente.
  let nce: Ponto | null = null;
  let iNce = -1;
  for (let i = 0; i < curva.length; i++) {
    const q = curva[i]!;
    if (q.p > c.p) continue;
    if (q.parcela > q.ambiente) {
      const ant = curva[i - 1];
      nce = ant && ant.p <= c.p && ant.parcela <= ant.ambiente ? cruzamento(ant, q) : { p: Math.min(q.p, c.p), t: q.parcela };
      // Já saturada e mais quente no próprio NCL: o NCE é o NCL.
      if (ant && ant.p > c.p) nce = { p: c.p, t: c.t };
      iNce = i;
      break;
    }
  }
  if (!nce) return { origem, ncl: c, nce: null, ne: null, cape: 0, cin: 0, curva };

  // NE: a última vez em que ela deixa de ser mais quente.
  let ne: Ponto | null = null;
  let iNe = curva.length - 1;
  for (let i = curva.length - 1; i > iNce; i--) {
    const q = curva[i]!;
    const ant = curva[i - 1]!;
    if (q.parcela <= q.ambiente && ant.parcela > ant.ambiente) {
      ne = cruzamento(ant, q);
      iNe = i;
      break;
    }
  }

  // Regra do trapézio em ln(p): E = Rd ∫ (Tp − Ta) d ln p.
  let cape = 0;
  let cin = 0;
  for (let i = 1; i < curva.length; i++) {
    const a = curva[i - 1]!;
    const b = curva[i]!;
    const da = a.parcela - a.ambiente;
    const db = b.parcela - b.ambiente;
    const dlnp = Math.log(a.p / b.p);
    // Trecho que troca de sinal: cada triângulo vai para o seu lado.
    const partes: [number, number][] =
      da * db < 0
        ? [
            [da, dlnp * (da / (da - db))],
            [db, dlnp * (db / (db - da))],
          ]
        : [[(da + db) / 2 === 0 ? 0 : da + db, dlnp]];
    for (const [soma, largura] of partes) {
      const area = da * db < 0 ? (RD * soma * largura) / 2 : (RD * soma * largura) / 2;
      if (area > 0 && i > iNce - 1 && i <= iNe) cape += area;
      else if (area < 0 && i <= iNce) cin += area;
    }
  }
  return { origem, ncl: c, nce, ne, cape: Math.max(0, cape), cin: Math.min(0, cin), curva };
}

/** Espessura da camada de mistura, a partir da superfície (m). */
export const CAMADA_DE_MISTURA_M = 500;

/**
 * A parcela da camada de mistura: a média da temperatura potencial e da razão
 * de mistura nos primeiros 500 m, levantada a partir da superfície.
 *
 * A média é ponderada pela pressão (regra do trapézio), como no
 * `mixed_layer` do MetPy. Um nível só na camada devolve ele mesmo.
 */
export function parcelaDeMistura(
  niveis: readonly { p: number; t: number; d: number; z: number }[],
  espessura: number = CAMADA_DE_MISTURA_M,
): { p: number; t: number; d: number } | null {
  if (!niveis.length) return null;
  const base = niveis[0]!;
  const camada = niveis.filter((n) => n.z - base.z <= espessura);
  if (camada.length < 2) return { p: base.p, t: base.t, d: base.d };
  let peso = 0;
  let theta = 0;
  let w = 0;
  const th = (n: { p: number; t: number }) => (n.t + 273.15) * Math.pow(1000 / n.p, KAPPA);
  const rm = (n: { p: number; d: number }) => razaoDeMistura(pressaoDeSaturacao(n.d), n.p);
  for (let i = 1; i < camada.length; i++) {
    const a = camada[i - 1]!;
    const b = camada[i]!;
    const dp = a.p - b.p;
    peso += dp;
    theta += ((th(a) + th(b)) / 2) * dp;
    w += ((rm(a) + rm(b)) / 2) * dp;
  }
  if (!(peso > 0)) return { p: base.p, t: base.t, d: base.d };
  const t = (theta / peso) * Math.pow(base.p / 1000, KAPPA) - 273.15;
  return { p: base.p, t, d: Math.min(t, orvalhoDaMistura(base.p, w / peso)) };
}

/**
 * A parcela do diagrama: a da camada de mistura dos primeiros 500 m.
 *
 * A primeira versão levantava a parcela MAIS INSTÁVEL (maior theta-e nos
 * primeiros 300 hPa). Medido em 28/09/2026 nos 38 perfis publicados, ela
 * ficava refém do nível da superfície — em Boa Vista o orvalho do primeiro
 * nível era 26,2 °C e o do seguinte, 10 hPa acima, 19,2 °C — e a energia saía
 * até 8× a do Wyoming. A média da camada não tem esse problema, e é a parcela
 * cujos números mais se aproximam dos índices que ele publica: erro mediano de
 * 14% na CAPE, contra 20% da mais instável, 33% da camada de 1000 m e 16% da
 * de 500 m com temperatura virtual.
 */
export function parcelaDoPerfil(niveis: readonly Nivel[], pTopo = 100): Parcela | null {
  const completos = niveisCompletos(niveis);
  const origem = parcelaDeMistura(completos);
  return origem ? levantar(completos, origem, pTopo) : null;
}

// ---------------------------------------------------------------------------
// Vento
// ---------------------------------------------------------------------------

/** Como uma barbela se desenha: bandeiras de 50 nós, traços de 10 e meio traço de 5. */
export interface Barbela {
  calmo: boolean;
  bandeiras: number;
  tracos: number;
  meio: boolean;
}

/** A velocidade é arredondada para o múltiplo de 5 nós mais próximo, como manda a convenção. */
export function barbela(nos: number): Barbela {
  const v = Math.round(Math.max(0, nos) / 5) * 5;
  if (v < 5) return { calmo: true, bandeiras: 0, tracos: 0, meio: false };
  const bandeiras = Math.floor(v / 50);
  const resto = v - bandeiras * 50;
  return { calmo: false, bandeiras, tracos: Math.floor(resto / 10), meio: resto % 10 >= 5 };
}

// ---------------------------------------------------------------------------
// Áreas de energia (para pintar)
// ---------------------------------------------------------------------------

export interface PontoDeArea {
  p: number;
  ambiente: number;
  parcela: number;
}

/**
 * As áreas entre a parcela e o ambiente, como o `shade_cape` e o `shade_cin`
 * do MetPy as pintam: positiva (parcela mais quente) do NCE ao NE, negativa
 * (parcela mais fria) da origem ao NCE. Cada área é um trecho contínuo, com as
 * pontas nos cruzamentos.
 */
export function areasDeEnergia(parcela: Parcela): { positivas: PontoDeArea[][]; negativas: PontoDeArea[][] } {
  const positivas: PontoDeArea[][] = [];
  const negativas: PontoDeArea[][] = [];
  if (!parcela.nce) return { positivas, negativas };
  const pNce = parcela.nce.p;
  const pNe = parcela.ne?.p ?? parcela.curva[parcela.curva.length - 1]!.p;

  const cruzar = (a: PontoDeArea, b: PontoDeArea): PontoDeArea => {
    const da = a.parcela - a.ambiente;
    const db = b.parcela - b.ambiente;
    const k = da === db ? 0 : da / (da - db);
    const t = a.parcela + (b.parcela - a.parcela) * k;
    return { p: Math.exp(Math.log(a.p) + (Math.log(b.p) - Math.log(a.p)) * k), ambiente: t, parcela: t };
  };

  let atual: PontoDeArea[] = [];
  let sinal = 0;
  const fechar = () => {
    if (atual.length >= 2 && sinal !== 0) (sinal > 0 ? positivas : negativas).push(atual);
    atual = [];
    sinal = 0;
  };
  /** A área vale neste nível? Positiva só entre o NCE e o NE; negativa só abaixo do NCE. */
  const vale = (s: number, p: number) => (s > 0 ? p <= pNce + 1e-6 && p >= pNe - 1e-6 : s < 0 ? p >= pNce - 1e-6 : false);

  for (let i = 0; i < parcela.curva.length; i++) {
    const q = parcela.curva[i]!;
    const d = q.parcela - q.ambiente;
    const s = d > 0 ? 1 : d < 0 ? -1 : 0;
    const ant = parcela.curva[i - 1];
    if (s !== sinal || !vale(s, q.p)) {
      // Troca de sinal: a área anterior termina no cruzamento, e a nova começa nele.
      const x = ant && sinal !== 0 && s !== 0 && s !== sinal ? cruzar(ant, q) : null;
      if (x && atual.length) atual.push(x);
      fechar();
      if (s !== 0 && vale(s, q.p)) {
        sinal = s;
        if (x) atual.push(x);
        atual.push(q);
      }
      continue;
    }
    atual.push(q);
  }
  fechar();
  return { positivas, negativas };
}
